/////////////////////////////////////////////
//Dependencies
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/////////////////////////////////////////////
//CSS
import "./css/Recruitment.css";

/////////////////////////////////////////////
//Components
import ParseText from "../Modules/parseText/ParseText";
import ReverseParseText from "../Modules/reverseParseText/ReverseParseText";

//////////////////////////////////////////////////////////////////////////////////////////

const valueIcons = {
    v1: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 18h6" />
            <path d="M10 22h4" />
            <path d="M12 2a7 7 0 0 0-4 12.7c.7.6 1 1.5 1 2.3v1h6v-1c0-.8.3-1.7 1-2.3A7 7 0 0 0 12 2Z" />
        </svg>
    ),
    v2: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
    ),
    v3: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
            <polyline points="16 7 22 7 22 13" />
        </svg>
    ),
    v4: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
    ),
};

const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

function Recruitment() {
    const { t } = useTranslation();
    const pageTitle = "Plassys ▪ " + t("footer.link4-3");
    const pageDescription = ReverseParseText(t("recruitment.text"));

    const valueKeys = ["v1", "v2", "v3", "v4"];
    const offerKeys = ["item1", "item2", "item3", "item4", "item5"];

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href="https://plassys.com/recruitment" />
            </Helmet>

            <div className="career-page">

                {/* HERO */}
                <section className="career-hero">
                    <div className="career-hero-bg" aria-hidden="true"></div>
                    <div className="career-hero-overlay" aria-hidden="true"></div>
                    <div className="career-hero-content">
                        <span className="career-hero-tag">{t("recruitment.tag")}</span>
                        <h1 className="career-hero-title">{t("recruitment.title")}</h1>
                        <p className="career-hero-subtitle">{t("recruitment.subtitle")}</p>
                    </div>
                </section>

                {/* INTRO */}
                <section className="career-intro">
                    <div className="career-intro-inner">
                        <h2 className="career-intro-title">{t("recruitment.motiv")}</h2>
                        <div className="career-intro-accent" aria-hidden="true"></div>
                        <ParseText text={t("recruitment.text")} classN={"career-intro-text"} />
                    </div>
                </section>

                {/* VALUES */}
                <section className="career-values">
                    <div className="career-section-header">
                        <h2 className="career-section-title">{t("recruitment.values.title")}</h2>
                        <p className="career-section-subtitle">{t("recruitment.values.subtitle")}</p>
                    </div>
                    <div className="career-values-grid">
                        {valueKeys.map((key, i) => (
                            <article className="career-value-card" key={key}>
                                <div className="career-value-icon">{valueIcons[key]}</div>
                                <h3 className="career-value-title">{t(`recruitment.values.${key}.title`)}</h3>
                                <p className="career-value-text">{t(`recruitment.values.${key}.text`)}</p>
                                <span className="career-value-number" aria-hidden="true">{`0${i + 1}`}</span>
                            </article>
                        ))}
                    </div>
                </section>

                {/* OFFER */}
                <section className="career-offer">
                    <div className="career-offer-inner">
                        <div className="career-offer-image">
                            <img src="img/assets/bureaux2.jpg" alt="Locaux PLASSYS Bestek" loading="lazy" />
                            <div className="career-offer-image-frame" aria-hidden="true"></div>
                        </div>
                        <div className="career-offer-content">
                            <div className="career-section-header career-section-header--left">
                                <h2 className="career-section-title">{t("recruitment.offer.title")}</h2>
                                <p className="career-section-subtitle">{t("recruitment.offer.subtitle")}</p>
                            </div>
                            <ul className="career-offer-list">
                                {offerKeys.map((key) => (
                                    <li className="career-offer-item" key={key}>
                                        <span className="career-offer-check"><CheckIcon /></span>
                                        <span className="career-offer-text">{t(`recruitment.offer.${key}`)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <section className="career-cta">
                    <div className="career-cta-inner">
                        <h2 className="career-cta-title">{t("recruitment.cta.title")}</h2>
                        <p className="career-cta-text">{t("recruitment.cta.text")}</p>
                        <Link to="mailto:recruitment@plassys.com" className="career-cta-button">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                <polyline points="22,6 12,13 2,6" />
                            </svg>
                            <span>{t("recruitment.cta.button")}</span>
                        </Link>
                        <p className="career-cta-info">{t("recruitment.cta.info")}</p>
                        <a href="mailto:recruitment@plassys.com" className="career-cta-email">recruitment@plassys.com</a>
                    </div>
                </section>

            </div>
        </>
    );
}
export default Recruitment;
